import { Injectable } from '@angular/core';
import {ApiService} from "../api/api.service";
import { PermissionsService } from '../permissions/permissions.service';
import { AlertBannerService, AlertType } from 'src/app/lib/alert-banner';
import {Observable, throwError, EMPTY, BehaviorSubject, of} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import { HttpClient, HttpParams } from "@angular/common/http";
import * as saveAs from 'file-saver';


@Injectable({
  providedIn: 'root'
})
export class RequestService {
  private backgroundChecksSubject = new BehaviorSubject<any | undefined>(undefined);
  private backgroundChecksCache = new Map<string, any>();

  constructor(
    private api: ApiService,
    private alertList: AlertBannerService,
    private permissions: PermissionsService,
    private http: HttpClient) {
  }

  createExit(recipientData: any): Observable<any> {
    const endpoint = 'check/exits/create';
    return this.api.post(endpoint, recipientData).pipe(map(res => res['data']));
  }

  deprecatedCreateReference(recipientData: any): Observable<any> {
    const endpoint = 'check/references/create';
    return this.api.post(endpoint, recipientData).pipe(map(res => res['data']));
  }

  createBackgroundCheck(recipientData: any, backgroundCheck=undefined, isExternalCheck=true): Observable<any> {
    const endpoint = 'check/external_checks/create';
    let params = new HttpParams();
    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }
    if (!isExternalCheck){
      params = params.set('is_external_check', isExternalCheck);
    }
    return this.api.post(endpoint, recipientData, { params }).pipe(map(res => res['data']));
  }

  createPulse(recipientData: any): Observable<any> {
    const endpoint = 'check/pulses/create';
    return this.api.post(endpoint, recipientData).pipe(map(res => res['data']));
  }

  retrieveDraftExit(draftUid: any, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, filter?: any): Observable<any> {
    let params = new HttpParams();
    if (rowsPerView != 0){
        params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber-1)));
    }
    if (queryData){
        params = params.set('query',queryData);
    }
    if (sortBy){
        params = params.set('sort',sortBy);
    }
    // if (initial_list) {
    //   params = params.set('initial_list', initial_list);
    // }
    if (sortOrder){
        params = params.set('sort_order',sortOrder);
    }
    if (filter){
      let filterList = Object.keys(filter).filter(f => filter[f]);
      params = params.set('filter', JSON.stringify(filterList));
    }
    const endpoint = 'check/exits/' + draftUid;
    return this.api.get(endpoint, {params}).pipe(map(res => res['data']));
  }

  retrieveDraftBackgroundCheck(draftUid: any, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, filter?: any, backgroundCheck=undefined, isExternalCheck = true): Observable<any> {
    let params = new HttpParams();
    if (rowsPerView != 0){
        params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber - 1)));
    }
    if (queryData) {
        params = params.set('query', queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    // if (initial_list) {
    //   params = params.set('initial_list', initial_list);
    // }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (filter) {
      let filterList = Object.keys(filter).filter(f => filter[f]);
      params = params.set('filter', JSON.stringify(filterList));
    }
    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }

    params = params.set('is_external_check', isExternalCheck);

    const endpoint = 'check/external_checks/' + draftUid;
    return this.api.get(endpoint, { params }).pipe(map(res => res['data']));
  }

  retrieveDraftPulse(draftUid: any, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, filter?: any): Observable<any> {
    let params = new HttpParams();
    if (rowsPerView != 0){
        params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber-1)));
    }
    if (queryData){
        params = params.set('query',queryData);
    }
    if (sortBy){
        params = params.set('sort',sortBy);
    }
    // if (initial_list) {
    //   params = params.set('initial_list', initial_list);
    // }
    if (sortOrder){
        params = params.set('sort_order',sortOrder);
    }
    if (filter){
      let filterList = Object.keys(filter).filter(f => filter[f]);
      params = params.set('filter', JSON.stringify(filterList));
    }
    const endpoint = 'check/pulses/' + draftUid;
    return this.api.get(endpoint, {params}).pipe(map(res => res['data']));
  }

  retrievePulseRecipients(checkUid: any, queryData?: any, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, filter?: any): Observable<any> {
    let params = new HttpParams();
    if (rowsPerView != 0){
        params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView*(pageNumber-1)));
    }
    if (queryData) {
      queryData.forEach(element => {
        params = params.append(element.key, element.value);
      });
    }
    if (sortBy){
        params = params.set('sort',sortBy);
    }
    if (sortOrder){
        params = params.set('sort_order',sortOrder);
    }
    const endpoint = 'check/pulses/recepients/' + checkUid;
    return this.api.get(endpoint, {params}).pipe(map(res => res['data']));
  }

  listHiringAccounts(): Observable<any> {
    const endpoint = 'check/references/company/hiring-accounts';
    return this.api.get(endpoint).pipe(map(res => res['data']));
  }

  metaExit(draftUid: any): Observable<any> {
    const endpoint = 'check/exits/' + draftUid +'/meta';
    return this.api.get(endpoint).pipe(map(res => res['data']));
  }

  deprecatedMetaReference(draftUid: any): Observable<any> {
    const endpoint = 'check/references/' + draftUid +'/meta';
    return this.api.get(endpoint).pipe(map(res => res['data']));
  }

  metaBackgroundCheck(draftUid: any, isExternalCheck=true): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid +'/meta';
    let params = new HttpParams();
    params = params.set('is_external_check', isExternalCheck);
    return this.api.get(endpoint, {params}).pipe(map(res => res['data']));
  }

  metaPulse(draftUid: any): Observable<any> {
    const endpoint = 'check/pulses/' + draftUid +'/meta';
    return this.api.get(endpoint).pipe(map(res => res['data']));
  }

  editExit(recipientData: any, draftUid: any): Observable<any> {
    const endpoint = 'check/exits/' + draftUid;
    return this.api.put(endpoint, recipientData).pipe(map(res => res['data']));
  }

  deprecatedEditReference(recipientData: any, draftUid: any): Observable<any> {
    const endpoint = 'check/references/' + draftUid;
    return this.api.put(endpoint, recipientData).pipe(map(res => res['data']));
  }

  editBackgroundChecks(recipientData: any, draftUid: any, backgroundCheck=undefined, isExternalCheck=true): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid;
    let params = new HttpParams();
    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }
    params = params.set('is_external_check', isExternalCheck);
    return this.api.put(endpoint, recipientData, { params }).pipe(map(res => res['data']));
  }

  editPulse(recipientData: any, draftUid: any): Observable<any> {
    const endpoint = 'check/pulses/' + draftUid;
    return this.api.put(endpoint, recipientData).pipe(map(res => res['data']));
  }

  appendExit(appendRow: any, draftUid: any): Observable<any> {
    const endpoint = 'check/exits/' + draftUid +'/append';
    return this.api.put(endpoint, appendRow).pipe(map(res => res['data']));
  }

  appendBackgroundCheck(appendRow: any, draftUid: any, backgroundCheck=undefined, isExternalCheck=true): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid +'/append';
    let params = new HttpParams();

    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }
    params = params.set('is_external_check', isExternalCheck);
    return this.api.put(endpoint, appendRow, { params }).pipe(map(res => res['data']));
  }

  deprecateupdateBackgroundChecksDraft(action, draftUid: any, checkOperations): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid +'/update';
    let params = new HttpParams();

    if (checkOperations.add && checkOperations.add.length > 0) {
      params = params.set('add', checkOperations.add.join(','));
    }

    if (checkOperations.remove && checkOperations.remove.length > 0) {
      params = params.set('remove', checkOperations.remove.join(','));
    }

    return this.api.put(endpoint, action, { params }).pipe(map(res => res['data']));
  }

  updateBackgroundChecksDraft(action, draftUid: any, checkOperations): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid +'/update';
    let params = new HttpParams();

    if (checkOperations.add && checkOperations.add.length > 0) {
      params = params.set('add', checkOperations.add.join(','));
    }

    if (checkOperations.remove && checkOperations.remove.length > 0) {
      params = params.set('remove', checkOperations.remove.join(','));
    }

    if (checkOperations.flows_add && checkOperations.flows_add.length > 0) {
      params = params.set('flows_add', checkOperations.flows_add.join(','));
    }

    if (checkOperations.flows_remove && checkOperations.flows_remove.length > 0) {
      params = params.set('flows_remove', checkOperations.flows_remove.join(','));
    }

    return this.api.put(endpoint, action, { params }).pipe(map(res => res['data']));
  }

  appendPulse(appendRow: any, draftUid: any): Observable<any> {
    const endpoint = 'check/pulses/' + draftUid +'/append';
    return this.api.put(endpoint, appendRow).pipe(map(res => res['data']));
  }

  removeExit(draftUid: any, idToRemove, sequenceToRemove = 0): Observable<any> {
    let params = new HttpParams();
    params = params.set('id', idToRemove);
    if(sequenceToRemove > 0) {
      params = params.set('sequence', sequenceToRemove.toString());
    }
    const endpoint = 'check/exits/' + draftUid + '/remove';
    return this.api.delete(endpoint, {params}).pipe(map(res => res['data']));
  }

  deprecatedRemoveReference(draftUid: any, idToRemove): Observable<any> {
    let params = new HttpParams();
    params = params.set('id', idToRemove);
    const endpoint = 'check/references/' + draftUid + '/remove';
    return this.api.delete(endpoint, {params}).pipe(map(res => res['data']));
  }

  removeBackgroundCheck(draftUid: any, idToRemove, backgroundCheck=undefined, isExternalCheck=true): Observable<any> {
    let params = new HttpParams();
    params = params.set('id', idToRemove);
    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }
    params = params.set('is_external_check', isExternalCheck);
    const endpoint = 'check/external_checks/' + draftUid + '/remove';
    return this.api.delete(endpoint, {params}).pipe(map(res => res['data']));
  }

  removePulse(draftUid: any, idToRemove, sequenceToRemove = 0): Observable<any> {
    let params = new HttpParams();
    params = params.set('id', idToRemove);
    if(sequenceToRemove > 0) {
      params = params.set('sequence', sequenceToRemove.toString());
    }
    const endpoint = 'check/pulses/' + draftUid + '/remove';
    return this.api.delete(endpoint, {params}).pipe(map(res => res['data']));
  }

  removeDraft(draftUid: any): Observable<any> {
    const endpoint = 'check/exits/' + draftUid;
    return this.api.delete(endpoint).pipe(map(res => res['data']));
  }

  removeDraftBackgroundCheck(draftUid: any, backgroundCheck=undefined, isExternalCheck=true): Observable<any> {
    const endpoint = 'check/external_checks/' + draftUid;
    let params = new HttpParams();
    if(backgroundCheck){
      params = params.set('bcg_check_short_name', backgroundCheck);
    }
    params = params.set('is_external_check', isExternalCheck);
    return this.api.delete(endpoint, { params }).pipe(map(res => res['data']));
  }

  removeDraftPulse(draftUid: any): Observable<any> {
    const endpoint = 'check/pulses/' + draftUid;
    return this.api.delete(endpoint).pipe(map(res => res['data']));
  }

  private handleError(error: any) {
    const errorMessage = 'Oops! Something went wrong. Our developers have been notified. Feel free to try again.';
    this.alertList.append([{
      type: AlertType.DANGER,
      message: $localize`${errorMessage}`,
      keepOnRouteChange: true
    }]);
    return throwError(error);
  }

  sendRequest(draftUid: any, questionnaireId: number, templateType: string, templates?: any) {
    let params = new HttpParams().set('template_type', templateType);
    let exit = {
      'package_id': questionnaireId,
    }
    if (templates.length > 0) exit['email_sms'] = templates;
    const endpoint = 'check/exits/'+ draftUid +'/send';
    return this.api.post(endpoint, exit, {params}).pipe(
      map(res => res['data']),
      catchError(this.handleError)
      )

  }

  sendExternalCheck(draftBackgroundUid: any, criteriaId?: any, requestSettings?: any,
                    requestType='Reference', packageId=undefined, template:any=undefined) {
    let reference = {
    }
    if(this.permissions.isFeatureActivated('ent-10605-request-templates') && template && template?.id)
    {
        reference['template_id'] = template.id
    }
    if(criteriaId){
      reference['criteria_id'] = criteriaId
    }
    if(requestSettings){
      reference['settings'] = requestSettings
    }
    if(packageId){
      reference['package_id'] = packageId
    }
    let params = new HttpParams();
    if(requestType){
      params = params.set('request_type', requestType)
    }
    const endpoint = 'check/external_checks/'+ draftBackgroundUid +'/send';
    return this.api.post(endpoint, reference,{params}).pipe
      (map(res => res['data']),
      catchError(error => this.handleError(error)));

  }


  sendPulse(draftUid: any, questionnaireId: number, templateType: string, templates?: any, payload?: any) {
    let params = new HttpParams().set('template_type', templateType);
    let pulse = {
      'package_id': questionnaireId,
    }
    if (templates.length > 0) pulse['email_sms'] = templates;
    if (Object.keys(payload).includes('expiry')){
      if(payload.isCutOffCustomized || payload.editPulseCustomised ){
        pulse['expiry'] = payload?.expiry
      }
    }
    if (Object.keys(payload).includes('schedule')){
      if(payload?.schedule?.scheduled_date && payload?.schedule?.scheduled_time){
        pulse['schedule'] = payload.schedule
      }
    }
    if(payload?.timezone){
      pulse['timezone'] = payload.timezone
    }


    const endpoint = 'check/pulses/' + draftUid + '/send';
    return this.api.post(endpoint, pulse, { params }).pipe(
      map(res => res['data']),
      catchError(error => this.handleError(error))
    );
  }

  getDraftCheckDetails(requestUid) {
    const body = {
      check_uid: requestUid
    }
    const path = `check/pulses/draft_pulse_check`
    return this.api.post(path, body)
  }
  getBackgroundChecks(checkName) {
    let params = new HttpParams();
    if(checkName){
      params = params.set('name', checkName)
    }

    const endpoint = 'check/external_checks/background-checks'
    return this.api.get(endpoint,{params}).pipe(map(res => res['data']),
      catchError(error => this.handleError(error)));
  }

  get BackgroundChecksSubject(){
    return this.backgroundChecksSubject.asObservable();
  }

  updateBackgroundChecksSubject(checks){
    this.backgroundChecksSubject.next(checks);
  }

  setBackgroundChecks(recipientUUID, checks, receipientId){
    const endpoint = 'check/external_checks/background-checks/' + recipientUUID
    let params = new HttpParams();
    params = params.set('id', receipientId)

    return this.api.put(endpoint,checks, {params}).pipe
      (map(res => res),
      catchError(error => this.handleError(error)));

  }

  getBackgroundChecksList(searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, filters?: any): Observable<any> {
    let params = new HttpParams();
    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.param, element.value);
      });
    }
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (sortString) {
      params = params.set('sort', sortString);
    }
    if (filters) {
      for (const param in filters) {
        params = params.set(param, filters[param]);
      }
    }

    const path = 'check/external_checks/background-checks';
    return this.api.get(path, { params }).pipe
      (map(res => res),
        catchError(error => this.handleError(error)));
  }

  getEnhancedBackgroundChecksList(searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, filters?: any, additionalParams?: any): Observable<any> {
    const cacheKey = this.createCacheKey(searchOptions, rowsPerView, pageNumber, sortString, filters, additionalParams);
    if (this.backgroundChecksCache.has(cacheKey)) {
      return of(this.backgroundChecksCache.get(cacheKey));
    }

    let params = new HttpParams();

    if (searchOptions) {
      searchOptions.forEach(element => {
        let mobileID = '';
        if (element.param === 'name' || element.param === 'all') {
          if ('mobileid - '.includes(element.value.toLowerCase())) {
            mobileID = 'ID -';
          }
        }
        let params_val = params.get(element.param);
        if (params_val) {
          params = params.delete(element.param);
          if (mobileID) {
            params = params.append(element.param, params_val + ',' + mobileID);
          }
          params = params.append(element.param, params_val + ',' + element.value);
        } else {
          if (mobileID) {
            params = params.append(element.param, mobileID);
          } else {
            params = params.append(element.param, element.value);
          }
        }
      });
    }

    if (rowsPerView !== 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }

    if (sortString) {
      params = params.set('sort', sortString);
    }

    // Handle filters
    if (filters) {
      for (const param in filters) {
        params = params.set(param, filters[param]);
      }
    }

    if (additionalParams) {
      for (const param in additionalParams) {
        params = params.set(param, additionalParams[param]);
      }
    }


    const path = 'check/external_checks/background-checks';
    return this.api.get(path, { params }).pipe(
      map(res => res),
      tap(data => this.backgroundChecksCache.set(cacheKey, data)),
      catchError(error => this.handleError(error))
    );
  }

  private createCacheKey(searchOptions: any, rowsPerView: number, pageNumber: number, sortString: string | undefined, filters: any, additionalParams: any): string {
    return JSON.stringify({ searchOptions, rowsPerView, pageNumber, sortString, filters, additionalParams });
  }


  getBackgroundChecksListWithFF(searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, filters?: any): Observable<any> {

    let params = new HttpParams();
    if (searchOptions) {
      searchOptions.forEach(element => {
        let mobileID = ''
        if(element.param == 'name' || element.param == 'all'){
            if('mobileid - '.includes(element.value.toLowerCase())) {
              mobileID = 'ID -'
            }
        }
        let params_val = params.get(element.param);
        if(params_val){
          params = params.delete(element.param);
          if(mobileID){
            params = params.append(element.param, params_val+','+mobileID);
          }
          params = params.append(element.param, params_val+','+element.value);
        }
        else{
          if(mobileID){
            params = params.append(element.param, params_val+','+mobileID);
          }
          else{
            params = params.append(element.param, element.value);
          }

        }
      });
    }
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (sortString) {
      params = params.set('sort', sortString);
    }
    if (filters) {
      for (const param in filters) {
        params = params.set(param, filters[param]);
      }
    }

    const path = 'check/external_checks/background-checks';
    return this.api.get(path, { params }).pipe
      (map(res => res),
        catchError(error => this.handleError(error)));
  }

  getAllBackgroundChecks(): Observable<any> {

    const path = 'check/external_checks/background-checks?detail=true';
    return this.api.get(path).pipe
      (map(res => res),
        catchError(error => this.handleError(error)));
  }

  pdfReportBlobGet(resource:string, options: any = {}): Observable<any> {
      return this.api.get(resource, options);
  }

  getPDFReport(url:string, check_short_name:string, person_uid:string, success: any = () => { }, error: any = () => { }): void {

    const successWrapped = (data: any) => {
      if(check_short_name.startsWith('id') || check_short_name === 'qualification_verification') {
        fetch(data)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.blob();
        })
        .then(blob => {
          // Use FileSaver.js to perform file save
          saveAs.saveAs(blob, `${person_uid}.pdf`);
          success();
        })
        .catch(err => {
          error(err.message);
        });
      } else {
        // Use FileSaver.js to perform file save
        saveAs.saveAs(data, `${person_uid}.pdf`);
        success();
      }
    };

    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    const responseType = check_short_name.startsWith('id') || check_short_name === 'qualification_verification' ? 'text' : 'blob'; // for rapidid checks response type is text and rest is blob

    const options = {
      params: {
        report_url: url,
        short_name: check_short_name
      },
      responseType
    }

    this.pdfReportBlobGet('check/external_checks/get-pdf-report', options).subscribe({
      next: successWrapped,
      error: errorWrapped
    });
  }
}
